/*
 * Copyright (c) 2020. Dave van Rijn Development
 */

import {getSetting} from '@dvrd/dvr-controls';
import {DefaultFetch, ResponseData} from '@dvrd/fetch';
import {URLS} from './api';
import {ISO_DATE_FORMAT} from './constants';
import {ContentMode, DocumentType, Language} from './enums';
import {ContentDef} from './types';
import IDate from '@dvrd/idate';

interface ID {
    id: string;
}

interface Callback {
    callback: (data: ResponseData, response: Response) => void;
}

interface WithData<T> {
    data: T;
}

interface WithPagination {
    limit?: number;
    after_id?: string | null;
    before_id?: string | null;
}

interface WithSearch {
    search: string | null;
}

type PaginatedSearch = WithPagination & WithSearch;

const requestSender = new DefaultFetch({baseURL: getSetting('platformUrl'), credentials: 'include'});

/*
 ================================================
 AUTH
 ================================================
 */

interface LoginData {
    email: string;
    password: string;
}

interface RegisterData extends LoginData {
    firstname: string;
    lastname?: string;
    flex?: Record<string, any>;
    settings?: Record<string, any>;
}

export function register(params: WithData<RegisterData> & Callback) {
    return requestSender.post({url: URLS.POST.REGISTER, ...params});
}

export function login(params: WithData<LoginData> & Callback) {
    return requestSender.post({url: URLS.POST.LOGIN, ...params});
}

export function logout(params: Callback) {
    return requestSender.get({url: URLS.GET.LOGOUT, ...params});
}

/*
 ================================================
 CONTENT
 ================================================
 */
interface LoadContent {
    language: Language;
    content_mode: ContentMode;
}

interface UpdateContent extends LoadContent {
    content: ContentDef;
}

interface PublishContent {
    language: Language | 'all';
}

export function loadContent(params: WithData<LoadContent> & Callback) {
    return requestSender.post({url: URLS.POST.LOAD_CONTENT, ...params});
}

export function updateContent(params: WithData<UpdateContent> & Callback) {
    return requestSender.put({url: URLS.PUT.UPDATE_CONTENT, ...params});
}

export function publishContent(params: WithData<PublishContent> & Callback) {
    return requestSender.post({url: URLS.POST.PUBLISH_CONTENT, ...params});
}

/*
 ================================================
 USER
 ================================================
 */

interface UpdateUser {

}

export const getUser = (params: Callback) => {
    return requestSender.get({url: URLS.GET.USER, ...params});
};

export const updateUser = (params: WithData<UpdateUser> & Callback) => {
    return requestSender.put({url: URLS.PUT.USER, ...params});
};

export const deleteUser = (params: ID & Callback) => {
    return requestSender.delete({url: URLS.DELETE.USER, ...params});
};

/*
 ================================================
 2FA
 ================================================
 */

interface Validate2FaData {
    token: string;
    jwt_token: string;
}

export function setupTwoFactor(params: Callback) {
    return requestSender.get({url: URLS.GET.GENERATE_TWO_FACTOR, ...params});
}

export function validateTwoFactor(params: WithData<Validate2FaData> & Callback) {
    return requestSender.post({url: URLS.POST.VALIDATE_TWO_FACTOR, ...params});
}

/*
 ================================================
 Header block
 ================================================
 */

interface GetNavBlocks extends PaginatedSearch {
    active?: boolean;
}

interface SetNavBlocks {
    blocks: Array<{
        label: string;
        background_color: string | null;
        color: string | null;
        url: string | null;
        active: boolean;
        index: number;
        image: string | null;
    }>;
}

export function getNavBlocks(params: WithData<GetNavBlocks> & Callback) {
    return requestSender.post({url: URLS.POST.GET_NAV_BLOCKS, ...params});
}

export function setNavBlocks(params: WithData<SetNavBlocks> & Callback) {
    return requestSender.post({url: URLS.POST.SET_NAV_BLOCKS, ...params});
}

/*
 ================================================
 Document
 ================================================
 */

interface WithDocumentID {
    document_id: string;
}

interface GetDocuments extends PaginatedSearch {
    active?: boolean;
    document_type?: DocumentType;
}

interface UploadDocument {
    file: File;
    label: string;
    active: boolean;
    document_type: DocumentType;
}

interface WithDocumentID {
    document_id: string;
}

interface UpdateDocument extends WithDocumentID {
    label?: string;
    active?: boolean;
}

export function uploadDocument(params: UploadDocument & Callback) {
    const data = new FormData();
    data.set('file', params.file);
    data.set('label', params.label);
    data.set('active', params.active ? 'true' : 'false');
    data.set('document_type', params.document_type);
    return requestSender.post({url: URLS.POST.UPLOAD_DOCUMENT, data, callback: params.callback});
}

export function getDocuments(params: WithData<GetDocuments> & Callback) {
    return requestSender.post({url: URLS.POST.GET_DOCUMENTS, ...params});
}

export function downloadDocument(params: WithDocumentID & Callback) {
    return requestSender.get({
        url: {url: URLS.GET.DOWNLOAD_DOCUMENT, document_id: params.document_id},
        callback: params.callback
    });
}

export function updateDocument(params: WithData<UpdateDocument> & Callback) {
    return requestSender.put({url: URLS.PUT.UPDATE_DOCUMENT, ...params});
}

export function deleteDocument(params: WithDocumentID & Callback) {
    return requestSender.delete(
        {url: {url: URLS.DELETE.DELETE_DOCUMENT, document_id: params.document_id}, callback: params.callback})
}

/*
 ================================================
 CALENDAR
 ================================================
 */

interface GetCalendarEvents {
    start_date: IDate;
    end_date: IDate;
}

export function getCalendarEvents(params: GetCalendarEvents & Callback) {
    const start = params.start_date.format(ISO_DATE_FORMAT);
    const end = params.end_date.format(ISO_DATE_FORMAT);
    return requestSender.get({
        url: URLS.GET.CALENDAR_EVENTS + `?start_date=${start}&end_date=${end}`,
        callback: params.callback
    });
}

/*
 ================================================
 CALENDAR ITEMS
 ================================================
 */

interface WithPeriod {
    from_date: string;
    to_date: string;
}

export function getCalendarItems(params: WithData<WithPeriod> & Callback) {
    return requestSender.post({url: URLS.POST.GET_CALENDAR_ITEMS, ...params});
}

/*
 ================================================
 CONTACT
 ================================================
 */

interface ContactRequest {
    name: string;
    email: string;
    message: string;
}

interface RegisterRequest {
    name_one: string;
    dob_one: string;
    name_two: string | null;
    dob_two: string | null;
    name_parent_one: string;
    tel_parent_one: string;
    email_parent_one: string;
    name_parent_two: string | null;
    tel_parent_two: string | null;
    email_parent_two: string | null;
    address: string;
    zip_code: string;
    city: string;
    other_registrars: string | null;
}

export function sendContactRequest(params: WithData<ContactRequest> & Callback) {
    return requestSender.post({url: URLS.POST.CONTACT_REQUEST, ...params});
}

export function sendRegisterRequest(params: WithData<RegisterRequest> & Callback) {
    return requestSender.post({url: URLS.POST.REGISTER_REQUEST, ...params});
}